import { alertController } from '@ionic/core';
import { store, persistor } from '../stores';
import { clearAuth, updateTokens } from '../stores/authSlice';
import { authService } from '../services/api/authService';
import { debugError } from './debug';
import { broadcastLogout, broadcastTokenRefresh } from './authBroadcastChannel';
import { clearOfflineUserData } from '../services/offline/clear-offline-data-on-logout';

const DEFAULT_AUTH_FETCH_TIMEOUT_MS = 30_000;

function mergeAbortSignals(signals: AbortSignal[]): AbortSignal {
  const active = signals.filter(Boolean);
  if (active.length === 0) {
    return new AbortController().signal;
  }
  if (active.length === 1) {
    return active[0];
  }
  const controller = new AbortController();
  const onAbort = (): void => controller.abort();
  for (const signal of active) {
    if (signal.aborted) {
      controller.abort();
      return controller.signal;
    }
    signal.addEventListener('abort', onAbort, { once: true });
  }
  return controller.signal;
}

function withFetchTimeout(
  options: RequestInit,
  timeoutMs: number,
): { options: RequestInit; clear: () => void } {
  const timeoutController = new AbortController();
  const timer = setTimeout(() => timeoutController.abort(), timeoutMs);
  const signals = [timeoutController.signal, options.signal].filter(
    (signal): signal is AbortSignal => signal != null,
  );
  return {
    options: { ...options, signal: mergeAbortSignals(signals) },
    clear: () => clearTimeout(timer),
  };
}

// Singleton promise per serializzare il refresh (una sola chiamata in corso)
let refreshPromise: Promise<boolean> | null = null;

// Timer per proactive refresh (aggiorna token prima della scadenza)
let _proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

function decodeJwtExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Programma un refresh silenzioso del token 2 minuti prima della scadenza.
 * Cancella qualsiasi timer precedente.
 */
export function scheduleProactiveRefresh(accessToken: string): void {
  if (_proactiveRefreshTimer !== null) clearTimeout(_proactiveRefreshTimer);
  const expiresAt = decodeJwtExpiry(accessToken);
  if (!expiresAt) return;
  const delay = expiresAt - Date.now() - 2 * 60 * 1000;
  if (delay <= 0) {
    void tryRefreshToken();
    return;
  }
  _proactiveRefreshTimer = setTimeout(() => {
    _proactiveRefreshTimer = null;
    void tryRefreshToken();
  }, delay);
}

export function cancelProactiveRefresh(): void {
  if (_proactiveRefreshTimer !== null) {
    clearTimeout(_proactiveRefreshTimer);
    _proactiveRefreshTimer = null;
  }
}

// Quando un altro tab aggiorna i token (via BroadcastChannel), riprogramma il refresh locale
if (typeof window !== 'undefined') {
  window.addEventListener('auth:tokens-synced', (e: Event) => {
    const accessToken = (e as CustomEvent<string>).detail;
    if (accessToken) scheduleProactiveRefresh(accessToken);
  });
}

/**
 * Tenta il refresh del token usando il refreshToken nello store.
 * Se più richieste ricevono 401 insieme, una sola chiamata a /api/auth/refresh viene eseguita.
 * Non effettua clearAuth su fallimento (gestito dal chiamante).
 * @returns true se il refresh è riuscito e i token sono aggiornati, false altrimenti
 */
export async function tryRefreshToken(): Promise<boolean> {
  const refreshToken = store.getState().auth.refreshToken;
  if (!refreshToken) {
    return false;
  }

  if (refreshPromise !== null) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const companyId = store.getState().auth.selectedCompanyId;
      const tokens = await authService.refresh(
        refreshToken,
        companyId ?? undefined,
      );
      store.dispatch(updateTokens(tokens));
      broadcastTokenRefresh(tokens.accessToken, tokens.refreshToken);
      scheduleProactiveRefresh(tokens.accessToken);
      return true;
    } catch (err) {
      debugError('Token refresh failed:', err);
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Flag globale per evitare loop infiniti - persiste anche dopo reload
const REDIRECT_FLAG_KEY = 'b2b_redirecting_to_login';
const REDIRECT_FLAG_TIMEOUT = 2000; // 2 secondi

let sessionExpiredModalShown = false;

/**
 * Mostra modale "Sessione scaduta" con opzione di rinnovare o uscire.
 * Se l'utente sceglie "Continua", tenta il refresh e lo lascia sulla pagina corrente.
 * Redirect al login solo se refresh fallisce o utente sceglie "Esci".
 */
export const handleSessionExpired = (): void => {
  if (window.location.pathname === '/login') {
    return;
  }
  if (sessionExpiredModalShown) {
    return;
  }
  sessionExpiredModalShown = true;

  const refreshToken = store.getState().auth.refreshToken;
  const hasRefreshToken = !!refreshToken;

  const showAlert = async (): Promise<void> => {
    const alert = await alertController.create({
      header: 'Sessione scaduta',
      message: hasRefreshToken
        ? 'La sessione è scaduta. Vuoi rinnovarla per continuare?'
        : 'La sessione è scaduta. Effettua nuovamente il login.',
      buttons: [
        {
          text: 'Esci',
          role: 'cancel',
          handler: () => {
            handleUnauthorized();
          },
        },
        ...(hasRefreshToken
          ? [
              {
                text: 'Rinnova sessione',
                handler: async () => {
                  if (!refreshToken) {
                    sessionExpiredModalShown = false;
                    handleUnauthorized();
                    return;
                  }
                  try {
                    const companyId = store.getState().auth.selectedCompanyId;
                    const tokens = await authService.refresh(
                      refreshToken,
                      companyId ?? undefined,
                    );
                    store.dispatch(updateTokens(tokens));
                    broadcastTokenRefresh(
                      tokens.accessToken,
                      tokens.refreshToken,
                    );
                    scheduleProactiveRefresh(tokens.accessToken);
                    sessionExpiredModalShown = false;
                    window.dispatchEvent(
                      new CustomEvent('app:token-refreshed'),
                    );
                  } catch (err) {
                    debugError('Token refresh failed:', err);
                    sessionExpiredModalShown = false;
                    handleUnauthorized();
                  }
                },
              },
            ]
          : []),
      ],
    });
    alert.onDidDismiss().then(() => {
      sessionExpiredModalShown = false;
    });
    try {
      await alert.present();
    } catch {
      sessionExpiredModalShown = false;
    }
  };

  showAlert();
};

/**
 * Gestisce il logout automatico quando si riceve un 401 irrecuperabile.
 * Pulisce lo stato Redux, notifica gli altri tab e reindirizza al login.
 *
 * IMPORTANTE: Questa funzione deve essere chiamata solo una volta.
 * Usa sessionStorage per evitare loop anche dopo reload.
 */
export const handleUnauthorized = (): void => {
  // Se siamo già sul login, non fare nulla
  if (window.location.pathname === '/login') {
    return;
  }

  // Controlla se stiamo già facendo redirect (usando sessionStorage per persistenza)
  const isRedirecting = sessionStorage.getItem(REDIRECT_FLAG_KEY);
  if (isRedirecting) {
    const timestamp = parseInt(isRedirecting, 10);
    const now = Date.now();
    // Se il flag è recente (meno di 2 secondi), ignora
    if (now - timestamp < REDIRECT_FLAG_TIMEOUT) {
      return;
    }
    // Altrimenti rimuovi il flag scaduto
    sessionStorage.removeItem(REDIRECT_FLAG_KEY);
  }

  cancelProactiveRefresh();
  broadcastLogout(); // Notifica gli altri tab di fare logout

  // Pulisci stato Redux PRIMA di impostare il flag
  // Questo assicura che quando la pagina si ricarica, isAuthenticated sarà false
  store.dispatch(clearAuth());

  // Imposta flag per prevenire chiamate multiple e per indicare al Login di non fare redirect
  sessionStorage.setItem(REDIRECT_FLAG_KEY, Date.now().toString());

  // Pulisci dati offline (SQLite, IndexedDB, localStorage, redux-persist) in background.
  // Il redirect avviene comunque, il cleanup non blocca il flusso.
  persistor.pause();
  clearOfflineUserData()
    .catch(() => {
      /* ignore: redirect comunque */
    })
    .finally(() => {
      persistor.persist();
      // Reindirizza al login usando window.location.replace per evitare di aggiungere alla history
      // e forzare un reload completo per pulire tutto lo stato (cache, query, ecc.)
      window.location.replace('/login');
    });
};

/**
 * Wrapper per fetch che su 401 tenta il refresh token e ritenta la richiesta.
 * Fa logout (handleUnauthorized) solo se il refresh fallisce o non c'è refreshToken.
 * I 403 (autorizzazione) vengono restituiti al chiamante senza tentare il refresh.
 *
 * @param url - URL della richiesta
 * @param options - Opzioni fetch standard (Authorization viene sostituito al retry)
 * @returns Promise<Response>
 */
function withCompanyContextHeaders(options: RequestInit): RequestInit {
  const headers = new Headers(options.headers ?? {});
  const companyId = store.getState().auth.selectedCompanyId;
  if (companyId && !headers.has('X-Company-Id')) {
    headers.set('X-Company-Id', companyId);
  }
  return { ...options, headers };
}

export const authFetch = async (
  url: string,
  options: RequestInit = {},
): Promise<Response> => {
  const { options: timedOptions, clear: clearTimeoutGuard } = withFetchTimeout(
    withCompanyContextHeaders(options),
    DEFAULT_AUTH_FETCH_TIMEOUT_MS,
  );

  try {
    let response = await fetch(url, timedOptions);

    if (response.status === 403) {
      return response;
    }

    if (response.status !== 401) {
      return response;
    }

    const refreshed = await tryRefreshToken();
    if (!refreshed) {
      handleSessionExpired();
      throw new Error('Unauthorized: sessione scaduta');
    }

    const newToken = store.getState().auth.accessToken;
    if (!newToken) {
      handleSessionExpired();
      throw new Error('Unauthorized: sessione scaduta');
    }

    const newHeaders = new Headers(options.headers ?? {});
    newHeaders.set('Authorization', `Bearer ${newToken}`);
    const companyId = store.getState().auth.selectedCompanyId;
    if (companyId) {
      newHeaders.set('X-Company-Id', companyId);
    }
    const retryWrapped = withFetchTimeout(
      { ...options, headers: newHeaders },
      DEFAULT_AUTH_FETCH_TIMEOUT_MS,
    );

    try {
      const retryResponse = await fetch(url, retryWrapped.options);
      if (retryResponse.status === 401) {
        handleSessionExpired();
        throw new Error('Unauthorized: sessione scaduta');
      }
      return retryResponse;
    } finally {
      retryWrapped.clear();
    }
  } catch (error) {
    if (
      error instanceof DOMException &&
      error.name === 'AbortError' &&
      !options.signal?.aborted
    ) {
      throw new Error(
        `Request timeout after ${DEFAULT_AUTH_FETCH_TIMEOUT_MS}ms`,
      );
    }
    throw error;
  } finally {
    clearTimeoutGuard();
  }
};
